/**
 * Canonical observation status and severity values.
 *
 * These mirror `backend/src/reusables/observationStatuses.ts`, which in turn
 * mirrors the `observations_status_enum` Postgres type. The
 * `@deep-consulting-solutions/observation-ui` package exports its own
 * `ObservationStatus` union, but it has drifted from the database — it is missing
 * the review statuses and carries a `TESTING` value the column cannot store — so
 * these are the values the app uses.
 *
 * Everything observation-related imports from here so the four pages cannot drift
 * apart again.
 */

export const OBSERVATION_STATUSES = [
  "PENDING",
  "INVESTIGATING",
  "PENDING_REVIEW",
  "IN_REVIEW",
  "NO_ISSUE",
  "RESOLVED",
] as const;

export type ObservationStatus = (typeof OBSERVATION_STATUSES)[number];

export const OBSERVATION_SEVERITIES = [
  "LOW",
  "MEDIUM",
  "HIGH",
  "CRITICAL",
] as const;

export type ObservationSeverity = (typeof OBSERVATION_SEVERITIES)[number];

export const OBSERVATION_STATUS_LABELS: Record<ObservationStatus, string> = {
  PENDING: "Pending",
  INVESTIGATING: "Investigating",
  PENDING_REVIEW: "Pending Review",
  IN_REVIEW: "In Review",
  NO_ISSUE: "No Issue",
  RESOLVED: "Resolved",
};

export const OBSERVATION_STATUS_COLORS: Record<ObservationStatus, string> = {
  PENDING: "blue",
  INVESTIGATING: "yellow",
  PENDING_REVIEW: "orange",
  IN_REVIEW: "cyan",
  NO_ISSUE: "gray",
  RESOLVED: "green",
};

export const OBSERVATION_SEVERITY_LABELS: Record<ObservationSeverity, string> =
  {
    LOW: "Low",
    MEDIUM: "Medium",
    HIGH: "High",
    CRITICAL: "Critical",
  };

export const OBSERVATION_SEVERITY_COLORS: Record<ObservationSeverity, string> =
  {
    LOW: "teal",
    MEDIUM: "yellow",
    HIGH: "orange",
    CRITICAL: "red",
  };

export const OBSERVATION_STATUS_OPTIONS = OBSERVATION_STATUSES.map((value) => ({
  value,
  label: OBSERVATION_STATUS_LABELS[value],
}));

export const OBSERVATION_SEVERITY_OPTIONS = OBSERVATION_SEVERITIES.map(
  (value) => ({
    value,
    label: OBSERVATION_SEVERITY_LABELS[value],
  })
);

export function isObservationStatus(
  value: string | null | undefined
): value is ObservationStatus {
  return !!value && (OBSERVATION_STATUSES as readonly string[]).includes(value);
}

export function isObservationSeverity(
  value: string | null | undefined
): value is ObservationSeverity {
  return (
    !!value && (OBSERVATION_SEVERITIES as readonly string[]).includes(value)
  );
}

/**
 * Falls back to the raw value so a status added to the database before the
 * frontend knows about it still renders readably instead of as a blank badge.
 */
export function observationStatusLabel(value: string): string {
  return isObservationStatus(value)
    ? OBSERVATION_STATUS_LABELS[value]
    : value.replace(/_/g, " ");
}

export function observationStatusColor(value: string): string {
  return isObservationStatus(value) ? OBSERVATION_STATUS_COLORS[value] : "gray";
}

export function observationSeverityColor(value: string): string {
  return isObservationSeverity(value)
    ? OBSERVATION_SEVERITY_COLORS[value]
    : "gray";
}
